// src/pages/Settings.js
import React, { useState, useRef, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiBell, FiUsers, FiEye, FiEdit2, FiTrash2, FiX, FiBriefcase, FiLock, FiRefreshCw, FiInfo } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import CheckboxGroup from '../components/CheckboxGroup'; 
import Modal from '../components/Modal';

// ✅ REFACTORED: MyProfileModal using shared Modal
const MyProfileModal = ({ onClose }) => {
    const { user, login } = useContext(AuthContext);
    const [profileData, setProfileData] = useState({ name: '', email: '' });
    
    // Password State
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => { 
        if (user) { 
            setProfileData({ name: user.name || '', email: user.email || '' }); 
            setPreviewUrl(user.profilePictureUrl || ''); 
        } 
    }, [user]);

    const handleChange = (e) => { 
        setProfileData(prev => ({ ...prev, [e.target.name]: e.target.value })); 
    };

    const handlePasswordChange = (e) => {
        setPasswords(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleFileChange = (e) => { 
        if (e.target.files && e.target.files[0]) { 
            const file = e.target.files[0]; 
            setSelectedFile(file); 
            setPreviewUrl(URL.createObjectURL(file)); 
        } 
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Client-side Validation
        if (showPasswordChange) {
            if (!passwords.current || !passwords.new) {
                alert("Please fill in current and new password.");
                return;
            }
            if (passwords.new !== passwords.confirm) {
                alert("New passwords do not match.");
                return;
            }
            if (passwords.new.length < 6) {
                alert("New password must be at least 6 characters.");
                return;
            }
        }

        const formData = new FormData();
        formData.append('name', profileData.name);
        formData.append('email', profileData.email);
        if (selectedFile) { formData.append('profilePicture', selectedFile); }
        
        if (showPasswordChange && passwords.new) {
            formData.append('currentPassword', passwords.current);
            formData.append('newPassword', passwords.new);
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/users/profile', { 
                method: 'PUT', 
                headers: { 'Authorization': `Bearer ${token}` }, 
                body: formData 
            });
            
            const result = await response.json();
            
            if (!response.ok) { 
                throw new Error(result.message || `Failed to update profile`); 
            }
            
            if (result.token) { 
                login(result.token); 
                alert("Profile updated successfully!"); 
                onClose(); 
            } else { 
                throw new Error("Update successful, but no new token received."); 
            }
        } catch (error) { 
            console.error(`Profile update error:`, error); 
            alert(`Error: ${error.message}`); 
        }
    };

    const userInitial = profileData.name ? profileData.name.charAt(0).toUpperCase() : '?';

    const footerButtons = (
        <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" form="profile-form" className="btn btn-primary">Save Changes</button>
        </>
    );

    return (
        <Modal 
            isOpen={true} 
            title="My Profile" 
            onClose={onClose} 
            footer={footerButtons}
        >
            <form id="profile-form" onSubmit={handleSubmit}>
                <div className="form-group profile-picture-container">
                    {previewUrl ? (
                        <img 
                            src={previewUrl} 
                            alt="Profile Preview" 
                            className="profile-picture-preview" 
                            onClick={() => fileInputRef.current.click()} 
                            onError={(e) => setPreviewUrl('')} 
                        />
                    ) : (
                        <div 
                            className="profile-picture-preview" 
                            onClick={() => fileInputRef.current.click()}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'var(--gray-200)',
                                color: 'var(--gray-600)',
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase'
                            }}
                        >
                            {userInitial}
                        </div>
                    )}
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }}/>
                    <button type="button" className="btn btn-link" onClick={() => fileInputRef.current.click()}>Change Picture</button>
                </div>
                
                <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input type="text" id="name" name="name" value={profileData.name} onChange={handleChange} className="form-input"/>
                </div>
                <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input type="email" id="email" name="email" value={profileData.email} disabled className="form-input" title="Email address cannot be changed."/>
                </div>

                <hr className="section-divider" style={{ margin: '1.5rem 0' }} />
                
                <div className="form-group">
                    <button 
                        type="button" 
                        className="btn btn-secondary btn-block" 
                        onClick={() => setShowPasswordChange(!showPasswordChange)}
                    >
                        {showPasswordChange ? "Cancel Password Change" : "Change Password"}
                    </button>
                </div>

                {showPasswordChange && (
                    <div style={{ backgroundColor: 'var(--gray-50)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                        <div className="form-group">
                            <label className="form-label" style={{ fontSize: '0.85rem' }}>Current Password</label>
                            <input type="password" name="current" value={passwords.current} onChange={handlePasswordChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label className="form-label" style={{ fontSize: '0.85rem' }}>New Password</label>
                            <input type="password" name="new" value={passwords.new} onChange={handlePasswordChange} className="form-input" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '0.85rem' }}>Confirm New Password</label>
                            <input type="password" name="confirm" value={passwords.confirm} onChange={handlePasswordChange} className="form-input" />
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
};

const NotificationToggle = ({ label, checked, onChange }) => ( 
    <div className="notification-toggle-item">
        <span>{label}</span>
        <label className="toggle-switch">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="slider"></span>
        </label>
    </div> 
);

// ✅ REFACTORED: NotificationsModal using shared Modal
const NotificationsModal = ({ onClose }) => {
    const { user, settings, updateSettings } = useContext(AuthContext);

    const [prefs, setPrefs] = useState({
        assignment: settings.notifications?.assignment !== false,
        requestAssignment: settings.notifications?.requestAssignment !== false,
    });

    const isPrivilegedUser = user?.roles?.some(role => ['Admin', 'Giga Admin'].includes(role));

    const handleToggle = (key) => {
        setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    };
    const handleSave = () => {
        updateSettings({ notifications: prefs });
        onClose();
    };

    const footerButtons = (
        <>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Save Preferences</button>
        </>
    );

    return ( 
        <Modal 
            isOpen={true} 
            title="Notification Preferences" 
            onClose={onClose} 
            footer={footerButtons}
        >
            <p className="form-group-description">Control browser notifications for real-time updates.</p>
            <div className="form-group">
                <NotificationToggle 
                    label="When a ticket is assigned to me" 
                    checked={prefs.assignment} 
                    onChange={() => handleToggle('assignment')} 
                />
                {isPrivilegedUser && (
                    <NotificationToggle 
                        label="When a tech requests assignment (Admins)" 
                        checked={prefs.requestAssignment} 
                        onChange={() => handleToggle('requestAssignment')} 
                    />
                )}
            </div>
        </Modal> 
    );
};

// ✅ REFACTORED: AppearanceModal using shared Modal
const AppearanceModal = ({ onClose, isDarkMode, toggleDarkMode, animationsEnabled, toggleAnimations }) => (
    <Modal
        isOpen={true}
        title="Appearance Settings"
        onClose={onClose}
        footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Done</button>}
    >
        <p className="form-group-description">Control how the dashboard looks and behaves.</p>
        <div className="form-group">
            {/* Dark Mode toggle disabled in source, keeping it disabled here */}
            <div className="notification-toggle-item">
                <span>Enable UI Animations</span>
                <label className="toggle-switch">
                    <input type="checkbox" checked={animationsEnabled} onChange={toggleAnimations} />
                    <span className="slider"></span>
                </label>
            </div>
        </div>
    </Modal>
);

// ✅ REFACTORED: DepartmentManagementModal using shared Modal
const DepartmentManagementModal = ({ isOpen, onClose, departments = [], onAdd, onUpdate, onDelete }) => {
    const initialState = { name: '', requireCheckoutImage: true };
    const [formData, setFormData] = useState(initialState);
    const [editingId, setEditingId] = useState(null);
    const formInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            handleCancelEdit(); 
            setTimeout(() => formInputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const handleStartEdit = (dept) => {
        setEditingId(dept._id);
        setFormData({
            name: dept.name,
            requireCheckoutImage: typeof dept.requireCheckoutImage === 'boolean' ? dept.requireCheckoutImage : true
        });
        formInputRef.current?.focus();
    };
    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData(initialState);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;

        if (editingId) {
            onUpdate(editingId, formData);
        } else {
            onAdd(formData);
        }
        handleCancelEdit();
    };

    return (
        <Modal 
            isOpen={isOpen} 
            title="Manage Departments" 
            onClose={onClose} 
            footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Done</button>}
        >
            <div className="items-list" style={{ maxHeight: '250px', marginBottom: '1.5rem', overflowY: 'auto' }}>
                {departments.length > 0 ? departments.map(dept => (
                    <div key={dept._id} className="list-item-row">
                        <div className="item-info">
                            <span className="item-title">{dept.name}</span>
                            {dept.requireCheckoutImage && (
                                <span className="item-subtitle" style={{ color: 'var(--red-600)', fontSize: '0.75rem' }}>
                                    Requires Photo on Checkout
                                </span>
                            )}
                        </div>
                        <div className="item-actions">
                            <button onClick={() => handleStartEdit(dept)} className="btn-icon-only"><FiEdit2 size={16} /></button>
                            <button onClick={() => onDelete(dept._id)} className="btn-icon-only btn-icon-danger"><FiTrash2 size={16} /></button>
                        </div>
                    </div>
                )) : (<p className="no-tasks-message">No departments added yet.</p>)}
            </div>
            <hr className="section-divider" />
            <form onSubmit={handleSubmit}>
                    <div className="form-group">
                    <label className="form-label">{editingId ? 'Edit Department' : 'Add New Department'}</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input 
                            ref={formInputRef} 
                            type="text" 
                            name="name" 
                            className="form-input" 
                            placeholder="e.g., Service Department" 
                            value={formData.name} 
                            onChange={handleChange} 
                        />
                        <button type="submit" className="btn btn-primary">{editingId ? 'Update' : 'Add'}</button>
                        {editingId && <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}><FiX/></button>}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', marginTop: '0.75rem', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            name="requireCheckoutImage"
                            className="custom-checkbox"
                            checked={formData.requireCheckoutImage}
                            onChange={handleChange}
                            style={{ cursor: 'pointer', width: '18px', height: '18px', padding: 0, flexShrink: 0 }}
                        />
                        Require photo attachment on checkout
                    </label>
                </div>
            </form>
        </Modal>
    );
};

const Settings = ({ apiCall, isDarkMode, toggleDarkMode, animationsEnabled, toggleAnimations, departments, onAddDepartment, onUpdateDepartment, onDeleteDepartment }) => {
  const [activeModal, setActiveModal] = useState(null);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const settingsItems = [
    { id: 'profile', icon: <FiUser size={32} />, title: "My Profile", description: "View and update your personal information." },
    { id: 'appearance', icon: <FiEye size={32} />, title: "Appearance", description: "Customize the look and feel of the application." },
    { id: 'notifications', icon: <FiBell size={32} />, title: "Notification Preferences", description: "Manage how you receive notifications." },
    ...(user?.roles?.includes('Giga Admin') ? [{ id: 'users', icon: <FiUsers size={32} />, title: "User Management", description: "Manage users, roles, and permissions.", path: '/settings/users' }] : []),
    ...(user?.roles?.includes('Giga Admin') ? [{ id: 'departments', icon: <FiBriefcase size={32} />, title: "Department Management", description: "Add or remove company departments." }] : []),
  ];

  const handleItemClick = (item) => {
    if (item.path) { navigate(item.path); } 
    else { setActiveModal(item.id); }
  };
  
  const handleCloseModal = () => setActiveModal(null);

  const renderModal = () => {
    switch (activeModal) {
      case 'profile': return <MyProfileModal onClose={handleCloseModal} />;
      case 'appearance': return <AppearanceModal onClose={handleCloseModal} isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} animationsEnabled={animationsEnabled} toggleAnimations={toggleAnimations} />;
      case 'notifications': return <NotificationsModal onClose={handleCloseModal} />;
      case 'departments': return <DepartmentManagementModal isOpen={true} onClose={handleCloseModal} departments={departments} onAdd={onAddDepartment} onUpdate={onUpdateDepartment} onDelete={onDeleteDepartment} />;
      default: return null;
    }
  };

  return (
    <div className="page-container">
        <div className="page-header"><h2 className="page-title">Settings</h2></div>
        
        <div className="community-grid-container">
            {settingsItems.map((item) => (
                <div key={item.id} className="community-card settings-card-item" onClick={() => handleItemClick(item)}>
                    <div className="community-card-icon">
                        {item.icon}
                    </div>
                    <h3 className="community-card-name">{item.title}</h3>
                    <p className="community-card-city" style={{ marginTop: '0.5rem', whiteSpace: 'normal', fontSize: '0.85rem' }}>
                        {item.description}
                    </p>
                </div>
            ))}
        </div>
      {renderModal()}
    </div>
  );
};

export default Settings;